import { Group, Tooltip } from '@mantine/core';
import Badge, { BADGE_LIGHT_STYLES } from '@ui/components/Badge/Badge';
import PricingPlanFeature from 'src/entities/pricing/pricingPlanFeature';
import WhiteBox from '../WhiteBox/WhiteBox';
import labelsPlaceholder from '../../assets/img/label-groups-placeholder.png';
import Button from '../../new_arch/components/Button/Button';
import LabelGroupsTooltip from './LabelGroupsTooltip/LabelGroupsTooltip';
import List from '../List/List';
import WorkspaceLabelGroupRow from './WorkspaceLabelGroupRow/WorkspaceLabelGroupRow';
import type LabelGroup from '../../entities/labelGroup';
import CreateOrEditLabelGroupModal from '../../modals/CreateOrEditLabelGroupModal/CreateOrEditLabelGroupModal';
import FeaturePaywall from '../../new_arch/modules/workspaces/components/FeaturePaywall/FeaturePaywall';
import useWorkspaceLabelGroups from './useWorkspaceLabelGroups';

import './WorkspaceLabelGroups.scss';

type LabelGroupsData = {
	workspace_label_groups: LabelGroup[],
	project_label_groups: LabelGroup[]
};

type WorkspaceLabelGroupsProps = {
	workspaceID: number,
	labelGroups: LabelGroupsData,
	onDeleteGroupClick: ( group: LabelGroup ) => void
}

const WorkspaceLabelGroups = ( {
	workspaceID, labelGroups, onDeleteGroupClick
}: WorkspaceLabelGroupsProps ) => {
	const {
		workspace,
		isFeatureAvailable,
		showNewGroupModal,
		groupToEdit,
		shouldDisableActionsButtons,
		shouldDisableGroupRow,
		onEditGroupClick,
		onNewGroupClick,
		onCloseNewGroupModal,
		onCloseEditGroupModal
	} = useWorkspaceLabelGroups( { workspaceID } );

	const workspaceGroups = labelGroups.workspace_label_groups || [];
	const projectGroups = labelGroups.project_label_groups || [];

	const renderHeader = () => (
		<div className="headLine">
			<div className="title">
				<Group spacing={10}>
					<h4>Label Groups</h4>
					<LabelGroupsTooltip />
					<FeaturePaywall feature={PricingPlanFeature.LABEL_GROUPS} workspace={workspace} origin="workspaceLabelGroups">
						{( { planName, paywallMessage } ) => (
							!isFeatureAvailable && (
								<Tooltip
									label={<div style={{ whiteSpace: 'pre-line', textAlign: 'center' }}>{paywallMessage}</div>}
									withArrow
									position="top"
									offset={4}
									withinPortal
									arrowPosition="center"
									disabled={isFeatureAvailable}
								>
									<Badge color="green" size="xs" variant="light" styles={BADGE_LIGHT_STYLES}>
										{`${planName}`}
									</Badge>
								</Tooltip>
							)
						)}
					</FeaturePaywall>
				</Group>
			</div>
			<div className="button-container">
				<Button onClick={onNewGroupClick} disabled={!isFeatureAvailable}>
					New Group
				</Button>
			</div>
		</div>
	);

	const renderTopBar = () => {
		if ( workspaceGroups.length === 0 && projectGroups.length === 0 ) {
			return null;
		}

		return (
			<div className="column-names">
				<div className="name">
					<h5>Name</h5>
				</div>
				<div className="labels">
					<h5>Labels</h5>
				</div>
				<div className="actions">
					<h5>Actions</h5>
				</div>
			</div>
		);
	};

	const renderGroupSection = ( sectionGroups: LabelGroup[], title: string ) => (
		<div className="label-group-section">
			<h4 className="section-title">{title}</h4>
			{sectionGroups.length === 0 ? (
				<div className="labels-placeholder" style={{ padding: '20px 0', borderTop: 'none' }}>
					<h3>No labels yet</h3>
					<p>
						Start creating groups and save time creating labels for your projects
					</p>
				</div>
			) : (
				/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
				/* @ts-expect-error */
				<List itemsSize={50}>
					{sectionGroups.map( group => (
						<WorkspaceLabelGroupRow
							key={group.id}
							group={group}
							onEditClick={() => onEditGroupClick( group )}
							onDeleteClick={() => onDeleteGroupClick( group )}
							editDisabled={shouldDisableGroupRow( group )}
							buttonsOnlyDisabled={shouldDisableActionsButtons( group )}
						/>
					) )}
				</List>
			)}
		</div>
	);

	const renderBody = () => {
		if ( workspaceGroups.length === 0 && projectGroups.length === 0 ) {
			return (
				<div className="labels-placeholder">
					<img src={labelsPlaceholder} width="230px" alt="labels-placeholder" />
					<h3>No labels yet</h3>
					<p>
						Start creating groups and save time creating labels for your projects
					</p>
				</div>
			);
		}

		return (
			<div className="label-groups">
				{renderGroupSection( workspaceGroups, 'Project Labels' )}
				{renderGroupSection( projectGroups, 'Folder + Asset Labels' )}
			</div>
		);
	}

	return (
		<div className="WorkspaceLabelGroups">
			{showNewGroupModal
				&& (
					<CreateOrEditLabelGroupModal
						workspaceID={workspaceID}
						mode="create"
						onClose={onCloseNewGroupModal}
					/>
				)}
			{groupToEdit && (
				<CreateOrEditLabelGroupModal
					workspaceID={workspaceID}
					mode="edit"
					labelGroup={groupToEdit}
					onClose={onCloseEditGroupModal}
				/>
			)}
			<WhiteBox>
				{renderHeader()}
				{renderTopBar()}
				{renderBody()}
			</WhiteBox>
		</div>
	);
};

export default WorkspaceLabelGroups;
